import React, { useMemo, useState, useEffect } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { gql, useQuery } from '@apollo/client'
import { DateTime } from 'luxon'
import JsonLd from 'components/atoms/json-ld'
import Divider from '@material-ui/core/Divider'
import LinearProgress from '@material-ui/core/LinearProgress'
import HeaderSection from './header-section'
import LeaderboardSection, { scoringExplanation } from './leaderboard-section'
import LocationSection from './location-section'
import ScheduleSection from './schedule-section'
import HowSection from './how-section'
import RegistrationCard from './registration-card'
import { RegistrationContainer } from 'components/program-marketing/registration-card'
import EventNotFound from 'components/program-marketing/event-not-found'
import Dialog from 'components/program-marketing/dialog'
import Container from 'components/program-marketing/container'
import { ContactCoachForm } from 'components/program-marketing/contact-coach'
import { getEventDates } from 'utils/marketingPages'
import { urls } from 'pages/family-cup-marketing/layout/menus'

export const CONTACT_COACH = gql`
  mutation ContactCoachFamilyCup($input: ContactCoachFamilyCupInput!) {
    contactCoachFamilyCup(input: $input) {
      message
      success
    }
  }
`

export const GET_PROGRAM = gql`
  query publicProgram($id: ID!) {
    publicProgram(id: $id) {
      id
      name
      description
      coachingFeeInCents
      pgaFeeInCents
      collectOnline
      maxTeams
      registrationOpen
      registrationClose
      registrationFull
      registrationStatus
      status
      programLocation {
        id
        name
        description
        coach {
          id
          name
          phone
          profilePhoto
        }
        facility {
          name
          address1
          address2
          city
          state
          zip
          latitude
          longitude
          phone
        }
        links {
          facebook
          instagram
        }
      }
      events {
        id
        date
        description
        details
        startTime
        endTime
        eventType
        numberOfHoles
        teamResults {
          eventId
          teamId
          score
        }
      }
      teams {
        id
        name
      }
    }
  }
`

const SectionDivider = () => (
  <Container style={{ paddingTop: 0, paddingBottom: 0 }}>
    <Divider />
  </Container>
)

const EventDetails = ({ setTitle }) => {
  const NotFound = () => <EventNotFound searchUrl={urls.search} />
  const { id } = useParams()
  const location = useLocation()
  const searchParams = useMemo(() => new URLSearchParams(location.search), [
    location,
  ])

  const { data, loading, error } = useQuery(GET_PROGRAM, {
    variables: { id },
  })

  const [activeDialog, setActiveDialog] = useState(
    searchParams.has('contactCoach') ? 'contactCoach' : null,
  )

  const viewProgramDesc = () => setActiveDialog('programDesc')
  const viewLocationDescription = () => setActiveDialog('locationDescription')
  const viewNoDates = () => setActiveDialog('noDates')
  const viewScoringRules = () => setActiveDialog('scoringRules')
  const viewAccessCodeRequired = () => setActiveDialog('accessCode')
  const viewTeamEligibility = () => setActiveDialog('teamEligibility')
  const viewContactCoach = () => setActiveDialog('contactCoach')
  const viewAnnualMembership = () => setActiveDialog('annualMembership')

  const closeActiveDialog = () => {
    setActiveDialog(null)
  }

  useEffect(() => {
    if (!loading && !error && data?.publicProgram) {
      setTitle(data?.publicProgram?.name)
    }
  }, [loading, error, data, setTitle])

  if (loading) return <LinearProgress color="secondary" />
  if (error || !data?.publicProgram) return <NotFound />

  const { programLocation, events, ...program } = data.publicProgram

  if (program.status !== 'PUBLISHED') return <NotFound />

  const eventDates = getEventDates(events)
  const startTime =
    events.length > 0
      ? DateTime.fromISO(events[0]?.startTime).toFormat('MMMM dd, y')
      : 'Event Schedule TBD'

  return (
    <>
      <JsonLd
        {...{
          '@context': 'https://schema.org',
          '@type': 'Event',
          name: program.name,
          startDate: startTime,
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          eventStatus: program.registrationStatus,
          image:
            'https://images.ctfassets.net/56u5qdsjym8c/5SWGO8vvLshekEP1mGo826/0345e17afa5d282c0bafb50174b06bf5/FG_programs_bg.png?h=250',
          location: {
            '@type': 'Place',
            name: programLocation?.name,
            address: {
              '@type': 'PostalAddress',
              streetAddress: programLocation.facility.address1,
              addressLocality: programLocation.facility.city,
              postalCode: programLocation.facility.zip,
              addressRegion: programLocation.facility.state,
              addressCountry: 'US',
            },
          },
          description: program.description,
          offers: {
            '@type': 'Offer',
            url: `https://www.pgafamilygolf.com/events/${program.id}`,
            price:
              program.coachingFeeInCents / 100 + program.pgaFeeInCents / 100,
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
          },
          organizer: {
            '@type': 'Person',
            name: programLocation.coach.name,
          },
        }}
      />
      <HeaderSection
        program={program}
        events={events}
        viewProgramDesc={viewProgramDesc}
        eventDates={eventDates}
      />

      <RegistrationContainer started={eventDates.started}>
        <RegistrationCard
          program={program}
          events={events}
          eventDates={eventDates}
          viewNoDates={viewNoDates}
          viewAccessCodeRequired={viewAccessCodeRequired}
          viewTeamEligibility={viewTeamEligibility}
          viewAnnualMembership={viewAnnualMembership}
        />
      </RegistrationContainer>

      {eventDates.started && (
        <LeaderboardSection
          program={program}
          events={events}
          viewScoringRules={viewScoringRules}
        />
      )}

      <LocationSection
        location={programLocation}
        viewLocationDescription={viewLocationDescription}
        viewContactCoach={viewContactCoach}
      />

      {events.length !== 0 && (
        <>
          <SectionDivider />
          <div id="schedule" />
          <ScheduleSection events={events} eventDates={eventDates} />
        </>
      )}

      <SectionDivider />
      <HowSection />

      <Dialog
        heading={program.name}
        open={activeDialog === 'programDesc'}
        onClose={closeActiveDialog}
        text={program.description}
      />
      <Dialog
        heading={programLocation.name}
        open={activeDialog === 'locationDescription'}
        onClose={closeActiveDialog}
        text={programLocation.description}
      />
      <Dialog
        heading="Why can't I view event dates?"
        open={activeDialog === 'noDates'}
        onClose={closeActiveDialog}
        text="Events will be created by the PGA Coach as the schedule is firmed up."
      />
      <Dialog
        heading={'Family Golf Scoring'}
        open={activeDialog === 'scoringRules'}
        onClose={closeActiveDialog}
        text={scoringExplanation}
      />
      <Dialog
        heading="Access Code Required"
        open={activeDialog === 'accessCode'}
        onClose={closeActiveDialog}
        text="This registration requires an Access Code provided by the Host Professional. Return to our search page to find open registration programs."
      />
      <Dialog
        heading="Team Eligibility &amp; Requirements"
        open={activeDialog === 'teamEligibility'}
        onClose={closeActiveDialog}
      >
        <ul>
          <li>2-4 players per team</li>
          <li>Minimum pair of one adult and one junior per team</li>
        </ul>
      </Dialog>
      <Dialog
        heading="Contact Coach"
        open={activeDialog === 'contactCoach'}
        onClose={closeActiveDialog}
      >
        <ContactCoachForm
          mutation={CONTACT_COACH}
          coachId={programLocation.coach.id}
          onCancel={closeActiveDialog}
          resultObjectName="contactCoachFamilyCup"
        />
      </Dialog>
      <Dialog
        title="Additional Fees"
        heading="Annual Membership Cost"
        open={activeDialog === 'annualMembership'}
        onClose={closeActiveDialog}
        text="PGA Family Golf Captains must pay an annual $25 membership fee, valid for the calendar year. Once paid, no additional membership fee is required for enrolling in other PGA Family Golf programs within the same calendar year."
        headingSecondary={'3% Processing Fee'}
        textSecondary="This fee covers the cost of processing your payment through our credit card processor and helps to support the cost of operating the platform for your use."
      />
    </>
  )
}

export default EventDetails
